import { useId, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Chip,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type {
  ActionSetId,
  LibraryUsageDto,
  MatchSetId,
} from "../../../api/types";
import { usageCount } from "../../../logic/library-usage";
import { UsedByGroup } from "./UsedByGroup";

export type UsedByChipProps = {
  usage: LibraryUsageDto;
  /** Library entries are edited in a dialog on this page, not on a route. */
  onOpenMatchSet: (id: MatchSetId) => void;
  onOpenActionSet: (id: ActionSetId) => void;
};

/**
 * "used by N" on a library entry, opening the list of the N: the rules and
 * other entries that cite it, each linking to its editor. "unused" is inert —
 * there is nothing to list.
 */
export const UsedByChip = ({
  usage,
  onOpenMatchSet,
  onOpenActionSet,
}: UsedByChipProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const popoverId = useId();
  const count = usageCount(usage);

  if (count === 0) {
    return <Chip size="small" variant="outlined" label="unused" />;
  }

  const close = () => setAnchor(null);
  const openEntry = (open: () => void) => () => {
    close();
    open();
  };

  return (
    <>
      <Chip
        size="small"
        variant="outlined"
        label={`used by ${count}`}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={anchor ? "true" : "false"}
        aria-controls={anchor ? popoverId : undefined}
      />
      <Popover
        id={popoverId}
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            role: "dialog",
            "aria-label": "Used by",
            sx: { maxWidth: "min(360px, calc(100vw - 32px))" },
          },
        }}
      >
        <Box sx={{ pb: 1 }}>
          {usage.rules.length > 0 && (
            <UsedByGroup title="Rules">
              {usage.rules.map((rule) => (
                <ListItem key={rule.id} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={`/admin/auto-transactions/${rule.id}`}
                    onClick={close}
                    sx={{ gap: 1 }}
                  >
                    <ListItemText primary={rule.name} />
                    {rule.broken && (
                      <Tooltip title="This rule can't run — open it to see why">
                        <Chip
                          size="small"
                          color="error"
                          icon={<WarningAmberIcon fontSize="small" />}
                          label="Broken"
                        />
                      </Tooltip>
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </UsedByGroup>
          )}
          {usage.matchSets.length > 0 && (
            <UsedByGroup title="Shared matches">
              {usage.matchSets.map((set) => (
                <ListItem key={set.id} disablePadding>
                  <ListItemButton
                    onClick={openEntry(() => onOpenMatchSet(set.id))}
                  >
                    <ListItemText primary={set.name} />
                  </ListItemButton>
                </ListItem>
              ))}
            </UsedByGroup>
          )}
          {usage.actionSets.length > 0 && (
            <UsedByGroup title="Shared actions">
              {usage.actionSets.map((set) => (
                <ListItem key={set.id} disablePadding>
                  <ListItemButton
                    onClick={openEntry(() => onOpenActionSet(set.id))}
                  >
                    <ListItemText primary={set.name} />
                  </ListItemButton>
                </ListItem>
              ))}
            </UsedByGroup>
          )}
        </Box>
      </Popover>
    </>
  );
};
